import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  User as UserIcon,
  Search,
  Filter,
  ArrowRight
} from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useTickets } from "@/hooks/useTickets";
import { Ticket } from "@/types/admin";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  open: { color: "bg-[#FFD93D]", icon: AlertCircle },
  in_progress: { color: "bg-[#4DA6FF] text-white", icon: Clock },
  resolved: { color: "bg-[#6FEDD6]", icon: CheckCircle2 },
  closed: { color: "bg-gray-200 text-gray-500", icon: CheckCircle2 },
};

const PRIORITY_CONFIG = {
  urgent: { color: "text-red-600 font-black" },
  high: { color: "text-orange-600 font-bold" },
  medium: { color: "text-[#4DA6FF] font-bold" },
  low: { color: "text-gray-400" },
};

export default function AdminTickets() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { getTickets, loading } = useTickets();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Status labels - must be inside component to access t()
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      open: t('admin.tickets.status.open'),
      in_progress: t('admin.tickets.status.inProgress'),
      resolved: t('admin.tickets.status.resolved'),
      closed: t('admin.tickets.status.closed'),
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority: string) => {
    const labels: Record<string, string> = {
      urgent: t('admin.tickets.priority.urgent'),
      high: t('admin.tickets.priority.high'),
      medium: t('admin.tickets.priority.medium'),
      low: t('admin.tickets.priority.low'),
    };
    return labels[priority] || priority;
  };

  const fetchTickets = useCallback(async () => {
    const data = await getTickets({ status: statusFilter, query: search });
    setTickets(data);
  }, [getTickets, statusFilter, search]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets();
    }, 500);
    return () => clearTimeout(timer);
  }, [search, fetchTickets]);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      <div className="mb-8 space-y-1">
        <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.tickets.title')}</h2>
        <p className="text-lg font-medium text-gray-500">{t('admin.tickets.subtitle')}</p>
      </div>

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative flex-1 md:max-w-md">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('admin.tickets.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border-2 border-black bg-white py-3 pl-12 pr-4 font-bold shadow-[2px_2px_0_#000] focus:shadow-[4px_4px_0_#000] focus:outline-none transition-all"
          />
        </div>

        <div className="flex items-center gap-3 overflow-x-auto pb-2 pr-2">
          {['all', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn(
                "whitespace-nowrap rounded-lg border-2 border-black px-4 py-2 font-black transition-all",
                statusFilter === s 
                  ? "bg-[#6FEDD6] shadow-[4px_4px_0_#000]" 
                  : "bg-white hover:bg-gray-50 hover:shadow-[2px_2px_0_#000]"
              )}
            >
              {s === 'all' ? t('admin.tickets.filters.all') : getStatusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border-2 border-black bg-white shadow-[8px_8px_0_#000]">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b-2 border-black bg-gray-50">
              <tr>
                <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.tickets.columns.ticketUser')}</th>
                <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.tickets.columns.priorityType')}</th>
                <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.tickets.columns.status')}</th>
                <th className="px-6 py-4 font-black uppercase tracking-wider">{t('admin.tickets.columns.lastUpdated')}</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-black/5">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-5 w-48 rounded bg-gray-200" />
                        <div className="h-4 w-32 rounded bg-gray-100" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="h-4 w-12 rounded bg-gray-200" />
                        <div className="h-3 w-16 rounded bg-gray-100" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 rounded-full bg-gray-200" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-24 rounded bg-gray-200" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-10 w-10 rounded-lg bg-gray-200" />
                    </td>
                  </tr>
                ))
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 font-bold">
                    {t('admin.tickets.noResults')}
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="font-black text-lg group-hover:text-[#4DA6FF] transition-colors">
                          {ticket.subject}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                          <UserIcon className="h-4 w-4" />
                          {ticket.name} ({ticket.email})
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={cn("text-sm", PRIORITY_CONFIG[ticket.priority as keyof typeof PRIORITY_CONFIG].color)}>
                          • {getPriorityLabel(ticket.priority)}
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          {t(`admin.tickets.type.${ticket.type}`, ticket.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border-2 border-black px-3 py-1 text-xs font-black",
                        STATUS_CONFIG[ticket.status as keyof typeof STATUS_CONFIG].color
                      )}>
                        {ticket.status === 'open' && <AlertCircle className="h-3 w-3" />}
                        {getStatusLabel(ticket.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-500">
                      {new Date(ticket.updated_at || ticket.created_at).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="rounded-lg border-2 border-black p-2 font-black shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:bg-[#FFD93D]">
                        <ArrowRight className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
